import { useEffect, useState } from 'react';
import { happyHourAPI, categoriesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Pencil, Trash2, Clock, Percent, Loader2, Sun, Check } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { id: 0, name: 'Lun' },
  { id: 1, name: 'Mar' },
  { id: 2, name: 'Mer' },
  { id: 3, name: 'Jeu' },
  { id: 4, name: 'Ven' },
  { id: 5, name: 'Sam' },
  { id: 6, name: 'Dim' }
];

const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function AdminHappyHours() {
  const [happyHours, setHappyHours] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    discount_percent: 20,
    category_ids: [],
    days_of_week: [],
    start_time: '17:00',
    end_time: '19:00',
    is_active: true
  });

  const loadData = async () => {
    try {
      const [hhRes, catRes] = await Promise.all([
        happyHourAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setHappyHours(hhRes.data);
      setCategories(catRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setForm({
      name: '',
      discount_percent: 20,
      category_ids: [],
      days_of_week: [],
      start_time: '17:00',
      end_time: '19:00',
      is_active: true
    });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.category_ids.length === 0) {
      toast.error('Sélectionnez au moins une catégorie');
      return;
    }
    if (form.days_of_week.length === 0) {
      toast.error('Sélectionnez au moins un jour');
      return;
    }
    
    setSaving(true);
    
    try {
      if (editing) {
        await happyHourAPI.update(editing.id, form);
        toast.success('Happy Hour mis à jour');
      } else {
        await happyHourAPI.create(form);
        toast.success('Happy Hour créé');
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce Happy Hour ?')) return;
    
    try {
      await happyHourAPI.delete(id);
      toast.success('Happy Hour supprimé');
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEdit = (hh) => {
    setEditing(hh);
    setForm({
      name: hh.name,
      discount_percent: hh.discount_percent,
      category_ids: hh.category_ids,
      days_of_week: hh.days_of_week,
      start_time: hh.start_time,
      end_time: hh.end_time,
      is_active: hh.is_active
    });
    setDialogOpen(true);
  };

  const toggleDay = (dayId) => {
    if (form.days_of_week.includes(dayId)) {
      setForm({ ...form, days_of_week: form.days_of_week.filter(d => d !== dayId) });
    } else {
      setForm({ ...form, days_of_week: [...form.days_of_week, dayId] });
    }
  };

  const toggleCategory = (catId) => {
    if (form.category_ids.includes(catId)) {
      setForm({ ...form, category_ids: form.category_ids.filter(c => c !== catId) });
    } else {
      setForm({ ...form, category_ids: [...form.category_ids, catId] });
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
            Happy Hours
          </h1>
          <p className="text-gray-500">Gérez vos promotions horaires</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-happy-hour-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Happy Hour
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier' : 'Nouveau'} Happy Hour</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="hh-name">Nom</Label>
                <Input
                  id="hh-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Happy Hour Cocktails"
                  required
                  data-testid="happy-hour-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="hh-discount">Réduction (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="hh-discount"
                    type="number"
                    min="1"
                    max="100"
                    value={form.discount_percent}
                    onChange={(e) => setForm({ ...form, discount_percent: parseFloat(e.target.value) })}
                    required
                    className="w-24"
                    data-testid="happy-hour-discount-input"
                  />
                  <span className="text-gray-500">%</span>
                </div>
              </div>

              <div>
                <Label>Jours</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DAYS.map((day) => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleDay(day.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        form.days_of_week.includes(day.id)
                          ? 'bg-[#7C3AED] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`day-${day.id}`}
                    >
                      {day.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hh-start">Heure de début</Label>
                  <Input
                    id="hh-start"
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    required
                    data-testid="happy-hour-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hh-end">Heure de fin</Label>
                  <Input
                    id="hh-end"
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    required
                    data-testid="happy-hour-end-input"
                  />
                </div>
              </div>

              <div>
                <Label>Catégories concernées</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {categories.map((cat) => {
                    const isChecked = form.category_ids.includes(cat.id);
                    return (
                      <div
                        key={cat.id}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isChecked
                            ? 'border-[#7C3AED] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCategory(cat.id);
                        }}
                        data-testid={`category-checkbox-${cat.id}`}
                      >
                        <div 
                          className="h-4 w-4 shrink-0 rounded-sm border border-primary shadow flex items-center justify-center"
                          style={{ backgroundColor: isChecked ? 'hsl(var(--primary))' : 'transparent' }}
                        >
                          {isChecked && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span className="text-sm font-medium">{cat.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="hh-active">Actif</Label>
                <Switch
                  id="hh-active"
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                  data-testid="happy-hour-active-switch"
                />
              </div>

              <Button type="submit" className="w-full" disabled={saving} data-testid="save-happy-hour-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Happy Hours List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {happyHours.map((hh) => (
          <Card key={hh.id} className={`border-0 shadow-sm ${!hh.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Sun className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ fontFamily: 'Manrope' }}>{hh.name}</h3>
                    <Badge className="bg-[#7C3AED] text-white">-{hh.discount_percent}%</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(hh)}
                    data-testid={`edit-happy-hour-${hh.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(hh.id)}
                    data-testid={`delete-happy-hour-${hh.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{hh.start_time} - {hh.end_time}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {hh.days_of_week?.map(d => (
                    <Badge key={d} variant="outline" className="text-xs">
                      {DAYS_FULL[d]}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
                  {hh.category_names?.map((name, idx) => (
                    <Badge key={idx} className="bg-gray-100 text-gray-700 text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>

              {!hh.is_active && (
                <Badge variant="outline" className="mt-2 text-amber-600 border-amber-600">
                  Inactif
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {happyHours.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          Aucun Happy Hour créé
        </div>
      )}
    </div>
  );
}
